import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import i18next from "i18next";
import { BsChevronRight } from "react-icons/bs";
import { ContainerComponent, MobileFloatingFooter, ProgressSteps, SecurityBadges } from "../../ui";
import { useToggle } from "../../../hooks";
import OrderFlightInfoComponent from "../OrderFlightInfoComponent";
import OrderIdAndStatusComponent from "../OrderIdAndStatusComponent";
import OrderAdditionalOptionsComponent from "../OrderAdditionalOptionsComponent";
import OrderPassengersComponent from "../OrderPassengersComponent";
import OrderPaymentFormComponent from "../OrderPaymentFormComponent";
import OrderPriceComponent from "../OrderPriceComponent";
import OrderRefundRequestComponent from "../OrderRefundRequestComponent";
import OrderSupportComponent from "../OrderSupportComponent";
import {
  Block,
  DisclaimerBanner,
  DisclaimerContent,
  DisclaimerHeader,
  DisclaimerIcon,
  DisclaimerText,
  DisclaimerTitle,
  DisclaimerToggle,
  PaymentThanks,
  PriceAndButtonsBlock,
  RoutesDataAndFormBlock,
  Wrapper,
} from "./style";
import { checkInAccessToPaymentStatus } from "../../../utils";
import OrderCopyAndCancelComponent from "../OrderCopyAndCancelComponent";

const OrderComponent = ({ sendPayment, cancelOrder, reloadOrder, notice, paymentThanks = false }) => {
  const { orderData, isLoad } = useSelector(state => state.order);
  const { currency } = useSelector(state => state.appConfigs);

  // после возврата из платёжки форму оплаты не показываем, даже если колбэк банка ещё не пришёл
  const isAccessToPayment = checkInAccessToPaymentStatus(orderData?.booking_status, orderData?.order_status) && !paymentThanks;

  const { isTrue: isDisclaimerOpen, toggle: toggleDisclaimer } = useToggle(true);
  const [voidStatus, setVoidStatus] = useState(false);
  const [showFloatingFooter, setShowFloatingFooter] = useState(true);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const payButtonRef = useRef(null);

  const handleMobilePayment = () => {
    const payButton = document.getElementById("order-pay-button");
    if (payButton) payButton.click();
  };

  // Hide floating footer when main payment button is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setShowFloatingFooter(!entry.isIntersecting);
        });
      },
      {
        root: null,
        threshold: 0.1,
      }
    );

    const findAndObserveButton = () => {
      const payButton = document.getElementById("order-pay-button");
      if (payButton) {
        payButtonRef.current = payButton;
        observer.observe(payButton);
      }
    };

    findAndObserveButton();
    const timeout = setTimeout(findAndObserveButton, 500);

    return () => {
      clearTimeout(timeout);
      if (payButtonRef.current) {
        observer.unobserve(payButtonRef.current);
      }
    };
  }, []);

  return (
    <Wrapper>
      <ContainerComponent>
        <ProgressSteps currentStep={3} />

        <DisclaimerBanner>
          <DisclaimerHeader onClick={toggleDisclaimer}>
            <DisclaimerIcon>⚠️</DisclaimerIcon>
            <DisclaimerTitle>{i18next.t("travel_disclaimer_title")}</DisclaimerTitle>
            <DisclaimerToggle $isOpen={isDisclaimerOpen}>
              <BsChevronRight />
            </DisclaimerToggle>
          </DisclaimerHeader>
          <DisclaimerContent $isOpen={isDisclaimerOpen}>
            <DisclaimerText>{i18next.t("travel_disclaimer")}</DisclaimerText>
          </DisclaimerContent>
        </DisclaimerBanner>

        <Block>
          <RoutesDataAndFormBlock>
            <OrderIdAndStatusComponent />
            <OrderPassengersComponent />
            <OrderFlightInfoComponent />
            {isAccessToPayment && !voidStatus && <OrderAdditionalOptionsComponent reloadOrder={reloadOrder} />}
            <SecurityBadges />
            <OrderCopyAndCancelComponent onCancelOrder={cancelOrder} isShowInSmallScreen />
            <OrderRefundRequestComponent isShowInSmallScreen />
            <OrderSupportComponent isShowInSmallScreen />
          </RoutesDataAndFormBlock>

          <PriceAndButtonsBlock>
            <OrderIdAndStatusComponent isShowInSmallScreen />
            {notice ? (
              <div style={{ padding: "12px 16px", borderRadius: 8, background: "#fff7e6", border: "1px solid #ffd591", color: "#ad4e00", fontSize: 14 }}>
                {notice}
              </div>
            ) : null}
            <OrderPriceComponent />
            {paymentThanks && (
              <PaymentThanks>
                <strong>{i18next.t("payment_thanks_title")}</strong>
                {i18next.t("payment_thanks_text")}
              </PaymentThanks>
            )}
            {isAccessToPayment && !voidStatus && (
              <OrderPaymentFormComponent sendPaymentRequest={sendPayment} onProcessingChange={setIsPaymentProcessing} />
            )}
            <OrderCopyAndCancelComponent onCancelOrder={cancelOrder} />
            <OrderRefundRequestComponent />
            <OrderSupportComponent />
          </PriceAndButtonsBlock>
        </Block>

        {isAccessToPayment && !voidStatus && (
          <MobileFloatingFooter
            totalPrice={orderData?.total_price_with_options ?? orderData?.total_price}
            currency={currency}
            onContinue={handleMobilePayment}
            isLoading={isPaymentProcessing}
            isVisible={showFloatingFooter}
            buttonText={i18next.t("pay_for_the_order")}
          />
        )}
      </ContainerComponent>
    </Wrapper>
  );
};

export default OrderComponent;
